import React, { useState, useEffect } from "react";
import { format, parse, isValid, startOfDay, endOfDay } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DateTimePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  showTime?: boolean;
  timeFormat?: "12" | "24";
  id?: string;
}

export default function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date and time",
  disabled = false,
  className,
  label,
  error,
  required = false,
  minDate,
  maxDate,
  showTime = true,
  timeFormat = "24",
  id
}: DateTimePickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Generate time options
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeValue = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        let displayValue = timeValue;

        if (timeFormat === "12") {
          const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          const ampm = hour >= 12 ? "PM" : "AM";
          displayValue = `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
        }

        options.push({
          value: timeValue,
          label: displayValue
        });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Initialize time from value
  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setSelectedTime(format(value, "HH:mm"));
    }
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);

    if (date && selectedTime) {
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const newDateTime = new Date(date);
      newDateTime.setHours(hours, minutes, 0, 0);
      onChange?.(newDateTime);
    } else if (date && !showTime) {
      onChange?.(date);
    } else {
      onChange?.(date);
    }

    if (!showTime) {
      setIsCalendarOpen(false);
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);

    if (selectedDate) {
      const [hours, minutes] = time.split(":").map(Number);
      const newDateTime = new Date(selectedDate);
      newDateTime.setHours(hours, minutes, 0, 0);
      onChange?.(newDateTime);
    }
  };

  const formatDisplayValue = () => {
    if (!selectedDate) return "";

    let dateStr = format(selectedDate, "MMM dd, yyyy");

    if (showTime && selectedTime) {
      const timeOption = timeOptions.find(opt => opt.value === selectedTime);
      if (timeOption) {
        dateStr += ` at ${timeOption.label}`;
      }
    }

    return dateStr;
  };

  const handleClear = () => {
    setSelectedDate(undefined);
    setSelectedTime("");
    onChange?.(undefined);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <div className="space-y-2">
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !selectedDate && "text-muted-foreground",
                error && "border-red-500"
              )}
              disabled={disabled}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formatDisplayValue() || placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="space-y-4 p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) => {
                  if (minDate && date < startOfDay(minDate)) return true;
                  if (maxDate && date > endOfDay(maxDate)) return true;
                  return false;
                }}
                initialFocus
              />

              {showTime && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Time</Label>
                  <Select value={selectedTime} onValueChange={handleTimeSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {timeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setIsCalendarOpen(false)}
                  className="flex-1"
                >
                  Done
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClear}
                  className="flex-1"
                >
                  Clear
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}